import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import {
  Database,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  BarChart3,
  Users,
  MapPin,
  Activity
} from 'lucide-react';
// import api from '../utils/firebase/api'; // Remove broken API import
import { db } from '../utils/firebase/client';
import { collection, getDocs } from 'firebase/firestore';
import { toast } from 'sonner';
import { getCompanyId } from '../utils/tenantContext';

interface DatabaseStats {
  status: string;
  timestamp: string;
  database: {
    wells: {
      total: number;
      active: number;
      inactive: number;
      maintenance: number;
    };
    users: {
      total: number;
      admins: number;
      standard: number;
    };
    entries: {
      total: number;
      byWell: Record<string, number>;
      dateRange: {
        earliest: string | null;
        latest: string | null;
      };
    };
    notifications: number;
  };
  schema: {
    version: string;
    initialized: boolean;
    indexes: string[];
  };
}

export default function DatabaseStatus() {
  const [stats, setStats] = useState<DatabaseStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const companyId = getCompanyId();

      if (!companyId) {
        throw new Error('No tenant ID context found');
      }

      // Fetch from SUBCOLLECTIONS directly
      const wellsRef = collection(db, `companies/${companyId}/wells`);
      const usersRef = collection(db, `companies/${companyId}/users`);

      const [wellsSnap, usersSnap] = await Promise.all([
        getDocs(wellsRef),
        getDocs(usersRef),
      ]);

      const wells = wellsSnap.docs.map(d => ({ ...d.data(), id: d.id, status: d.data().status }));
      const users = usersSnap.docs.map(d => ({ ...d.data(), id: d.id, role: d.data().role }));

      const statsData: DatabaseStats = {
        status: 'healthy',
        timestamp: new Date().toISOString(),
        database: {
          wells: {
            total: wells.length || 0,
            active: wells.filter((w: any) => w.status === 'active').length || 0,
            inactive: wells.filter((w: any) => w.status === 'inactive').length || 0,
            maintenance: wells.filter((w: any) => w.status === 'maintenance').length || 0,
          },
          users: {
            total: users.length || 0,
            admins: users.filter((u: any) => u.role === 'admin' || u.role === 'superadmin').length || 0,
            standard: users.filter((u: any) => u.role === 'user' || u.role === 'pumper').length || 0,
          },
          entries: {
            total: 0,
            byWell: {},
            dateRange: {
              earliest: null,
              latest: null,
            },
          },
          notifications: 0,
        },
        schema: {
          version: '1.0',
          initialized: true,
          indexes: ['wells', 'users', 'notifications'],
        },
      };

      setStats(statsData);
    } catch (err) {
      console.error('Database health check unavailable:', err);
      // Don't show error to user for background check, but set default empty state
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    return status === 'healthy' ? 'text-green-600' : 'text-red-600';
  };

  const getStatusIcon = (status: string) => {
    return status === 'healthy' ? CheckCircle : AlertTriangle;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span>Loading database status...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert className="border-red-200 bg-red-50">
        <AlertTriangle className="h-4 w-4 text-red-600" />
        <AlertDescription className="text-red-800">
          Error loading database status: {error}
        </AlertDescription>
      </Alert>
    );
  }

  if (!stats) {
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          No database statistics available
        </AlertDescription>
      </Alert>
    );
  }

  const StatusIcon = getStatusIcon(stats.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Database className="h-6 w-6 text-blue-600" />
          <h2>Database Status</h2>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadStats}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Overall Status */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center space-x-2">
            <StatusIcon className={`h-5 w-5 ${getStatusColor(stats.status)}`} />
            <span>System Status</span>
            <Badge variant={stats.status === 'healthy' ? 'default' : 'destructive'}>
              {stats.status.toUpperCase()}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Schema Version</p>
              <p className="font-medium">{stats.schema.version}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Last Updated</p>
              <p className="font-medium">{formatDate(stats.timestamp)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Wells */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Wells</CardTitle>
            <MapPin className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.database.wells.total}</div>
            <div className="space-y-1 mt-4">
              <div className="flex justify-between text-sm">
                <span>Active:</span>
                <Badge variant="default">{stats.database.wells.active}</Badge>
              </div>
              <div className="flex justify-between text-sm">
                <span>Inactive:</span>
                <Badge variant="secondary">{stats.database.wells.inactive}</Badge>
              </div>
              <div className="flex justify-between text-sm">
                <span>Maintenance:</span>
                <Badge variant="outline">{stats.database.wells.maintenance}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Users */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Users</CardTitle>
            <Users className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.database.users.total}</div>
            <div className="space-y-1 mt-4">
              <div className="flex justify-between text-sm">
                <span>Admins:</span>
                <Badge variant="default">{stats.database.users.admins}</Badge>
              </div>
              <div className="flex justify-between text-sm">
                <span>Standard:</span>
                <Badge variant="secondary">{stats.database.users.standard}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Entries */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Data Entries</CardTitle>
            <Activity className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.database.entries.total}</div>
            <div className="space-y-1 mt-4">
              <div className="text-sm">
                <p className="text-gray-600">Date Range:</p>
                <p className="font-medium">
                  {formatDate(stats.database.entries.dateRange.earliest)}
                  {' - '}
                  {formatDate(stats.database.entries.dateRange.latest)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Wells with Entry Counts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5" />
            <span>Wells Entry Distribution</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(stats.database.entries.byWell).length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {Object.entries(stats.database.entries.byWell).map(([wellId, count]) => (
                <div key={wellId} className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 truncate">{wellId}</p>
                  <p className="text-lg font-bold text-blue-600">{count}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No entry data available</p>
          )}
        </CardContent>
      </Card>

      {/* Schema Information */}
      <Card>
        <CardHeader>
          <CardTitle>Schema Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span>Initialized:</span>
              <Badge variant={stats.schema.initialized ? 'default' : 'destructive'}>
                {stats.schema.initialized ? 'Yes' : 'No'}
              </Badge>
            </div>
            <Separator />
            <div>
              <p className="font-medium mb-2">Available Indexes:</p>
              <div className="flex flex-wrap gap-2">
                {stats.schema.indexes.map((index) => (
                  <Badge key={index} variant="outline">
                    {index}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}